// Territoria — canonical achievement ladders.
// Tiered medals progress through Bronze → Diamond as players hit thresholds.
// Special achievements are one-off insignias awarded for unique events.

import {
  Medal,
  Flag,
  Coins,
  Factory,
  Package,
  Landmark,
  HardHat,
  Sparkles,
  Compass,
  Crown,
  Gem,
  Briefcase,
  MapPin,
  Trophy,
  ShieldCheck,
} from "lucide-react";

export const TIER_ORDER = ["Bronze", "Silver", "Gold", "Platinum", "Diamond"];

export const TIER_META = {
  Bronze: {
    color: "#cd7f32",
    glow: "rgba(205,127,50,0.55)",
    soft: "rgba(205,127,50,0.12)",
    ring: "#5e3e1f",
  },
  Silver: {
    color: "#d6deec",
    glow: "rgba(214,222,236,0.55)",
    soft: "rgba(214,222,236,0.10)",
    ring: "#3a4258",
  },
  Gold: {
    color: "#f3c552",
    glow: "rgba(243,197,82,0.6)",
    soft: "rgba(243,197,82,0.10)",
    ring: "#7a5a1c",
  },
  Platinum: {
    color: "#79e6ff",
    glow: "rgba(121,230,255,0.55)",
    soft: "rgba(121,230,255,0.10)",
    ring: "#1d5870",
  },
  Diamond: {
    color: "#d978ff",
    glow: "rgba(217,120,255,0.6)",
    soft: "rgba(217,120,255,0.10)",
    ring: "#6b2a8c",
  },
};

// Helpers
const formatNum = (n) => n.toLocaleString("en-US");
const formatMoney = (n) => `$${n.toLocaleString("en-US")}`;

export const TIERED_BADGES = [
  {
    slug: "military-service",
    name: "Military Service",
    icon: Medal,
    category: "Combat",
    description: "Participate in battles.",
    metric: "Battles",
    tiers: [
      { tier: "Bronze", value: 10, label: formatNum(10) },
      { tier: "Silver", value: 100, label: formatNum(100) },
      { tier: "Gold", value: 1000, label: formatNum(1000) },
      { tier: "Platinum", value: 10000, label: formatNum(10000) },
      { tier: "Diamond", value: 100000, label: formatNum(100000) },
    ],
  },
  {
    slug: "conquest",
    name: "Conquest",
    icon: Flag,
    category: "Combat",
    description: "Participate in territory captures.",
    metric: "Territories",
    tiers: [
      { tier: "Bronze", value: 1, label: formatNum(1) },
      { tier: "Silver", value: 10, label: formatNum(10) },
      { tier: "Gold", value: 100, label: formatNum(100) },
      { tier: "Platinum", value: 1000, label: formatNum(1000) },
      { tier: "Diamond", value: 10000, label: formatNum(10000) },
    ],
  },
  {
    slug: "economy",
    name: "Economy",
    icon: Coins,
    category: "Wealth",
    description: "Generate market revenue.",
    metric: "Revenue",
    tiers: [
      { tier: "Bronze", value: 100000, label: formatMoney(100000) },
      { tier: "Silver", value: 1000000, label: formatMoney(1000000) },
      { tier: "Gold", value: 10000000, label: formatMoney(10000000) },
      { tier: "Platinum", value: 100000000, label: formatMoney(100000000) },
      { tier: "Diamond", value: 1000000000, label: formatMoney(1000000000) },
    ],
  },
  {
    slug: "industry",
    name: "Industry",
    icon: Factory,
    category: "Industry",
    description: "Produce goods.",
    metric: "Produced",
    tiers: [
      { tier: "Bronze", value: 10000, label: formatNum(10000) },
      { tier: "Silver", value: 100000, label: formatNum(100000) },
      { tier: "Gold", value: 1000000, label: formatNum(1000000) },
      { tier: "Platinum", value: 10000000, label: formatNum(10000000) },
      { tier: "Diamond", value: 100000000, label: formatNum(100000000) },
    ],
  },
  {
    slug: "logistics",
    name: "Logistics",
    icon: Package,
    category: "Industry",
    description: "Donate resources to national stockpile.",
    metric: "Resources",
    tiers: [
      { tier: "Bronze", value: 10000, label: formatNum(10000) },
      { tier: "Silver", value: 100000, label: formatNum(100000) },
      { tier: "Gold", value: 1000000, label: formatNum(1000000) },
      { tier: "Platinum", value: 10000000, label: formatNum(10000000) },
      { tier: "Diamond", value: 100000000, label: formatNum(100000000) },
    ],
  },
  {
    slug: "political",
    name: "Political",
    icon: Landmark,
    category: "Politics",
    description: "Days spent in government positions.",
    metric: "Days",
    tiers: [
      { tier: "Bronze", value: 7, label: "7" },
      { tier: "Silver", value: 30, label: "30" },
      { tier: "Gold", value: 90, label: "90" },
      { tier: "Platinum", value: 365, label: "365" },
      { tier: "Diamond", value: 1000, label: "1,000" },
    ],
  },
  {
    slug: "equipment",
    name: "Equipment",
    icon: HardHat,
    category: "Combat",
    description: "Highest equipment set owned.",
    metric: "Set",
    tiers: [
      { tier: "Bronze", value: "Recruit", label: "Recruit Set" },
      { tier: "Silver", value: "Ranger", label: "Ranger Set" },
      { tier: "Gold", value: "Infantry", label: "Infantry Set" },
      { tier: "Platinum", value: "Operator", label: "Operator Set" },
      { tier: "Diamond", value: "Sovereign", label: "Sovereign Set" },
    ],
  },
];

export const SPECIAL_BADGES = [
  {
    slug: "founder",
    name: "Founder",
    icon: Sparkles,
    accent: "#ffd870",
    description: "Joined during Beta.",
  },
  {
    slug: "pioneer",
    name: "Pioneer",
    icon: Compass,
    accent: "#79e6ff",
    description: "Created account during launch month.",
  },
  {
    slug: "president",
    name: "President",
    icon: Crown,
    accent: "#f3c552",
    description: "Served as President.",
  },
  {
    slug: "prime-minister",
    name: "Prime Minister",
    icon: Gem,
    accent: "#9ef0ff",
    description: "Served as Prime Minister.",
  },
  {
    slug: "minister",
    name: "Minister",
    icon: Briefcase,
    accent: "#a8b5d1",
    description: "Served as a Cabinet Minister.",
  },
  {
    slug: "governor",
    name: "Governor",
    icon: MapPin,
    accent: "#7eff9a",
    description: "Controlled a Territory.",
  },
  {
    slug: "kingmaker",
    name: "Kingmaker",
    icon: Trophy,
    accent: "#ff8a4a",
    description: "Helped country achieve Rank #1.",
  },
  {
    slug: "sovereign",
    name: "Sovereign",
    icon: ShieldCheck,
    accent: "#d978ff",
    description: "One of the first players to obtain a full Sovereign Set.",
  },
];
